import type { EnhancedStore } from '@reduxjs/toolkit';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { v7 as uuidv7 } from 'uuid';
import { SessionStore } from '../services/SessionStore';
import { appSettings } from '../settings';
import type { AppState } from '../store/AppStore';
import { resetBusy, setBusy } from '../store/states/BusySlice';
import { authSettings } from './OidcSettings';

//This method returns the oidc user token from the session storage
export const getAuthToken = (): any => {
  const sessionKey = `oidc.user:${authSettings.AuthorityUrl}:${authSettings.ClientId}`;
  return SessionStore.get(sessionKey)?.access_token;
};

//Create the axios client with  pdm api url
const AxiosClient = axios.create({
  baseURL: appSettings.PdmEndpoint,
  timeout: 60000,
  withCredentials: false,
});

let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

export const setupAxiosInterceptors = (store: EnhancedStore<AppState>): void => {
  if (requestInterceptorId !== null) {
    AxiosClient.interceptors.request.eject(requestInterceptorId);
  }
  if (responseInterceptorId !== null) {
    AxiosClient.interceptors.response.eject(responseInterceptorId);
  }

  //Added the axios request interceptors with custom headers
  requestInterceptorId = AxiosClient.interceptors.request.use((config) => {
    //show the loader based on skipLoader header value
    if (!config.headers.skipLoader) {
      store.dispatch(setBusy());
    }

    //delete the skip loader header to restrict to pass to the api
    config.headers.delete('skipLoader');

    const token = getAuthToken();
    config.headers['X-CORRELATION-ID'] = uuidv7();
    config.headers['Context'] = appSettings.Context;
    config.headers['Content-Type'] = 'application/json; charset=utf-8';
    config.headers['Accept'] = 'application/json';
    config.headers['X-Requested-With'] = 'CfLM-PHC-PDM';
    if (appSettings.STAuth) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  });

  //Added the axios response interceptors
  responseInterceptorId = AxiosClient.interceptors.response.use(
    (response: AxiosResponse) => {
      store.dispatch(resetBusy());
      return response;
    },
    (error: AxiosError) => {
      store.dispatch(resetBusy());
      return Promise.reject(error);
    }
  );
};

export default AxiosClient;
